import { onBeforeUnmount, nextTick, ref, watch } from 'vue';

import { SCROLL_BUTTON_THRESHOLD } from '~/constants/conversationModels';

// Scrolls each submitted message to the top of the view and lets its reply flow down below it. The view
// follows the stream only after the scroll button is used, until the user scrolls up. The spacer under the
// list fills the view below that message until the reply does.
export function useTopAnchoredScroll(messages) {
  const content = ref(null);
  const list = ref(null);
  const spacerHeight = ref(0);
  const showScrollButton = ref(false);

  let anchorIndex = -1;
  let pendingAnchor = false;
  let pendingScrollToBottom = true;
  let followToBottom = false;
  let lastScrollTop = 0;
  let lastScrollHeight = 0;

  const checkScrollPosition = () => {
    if (!content.value) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = content.value;
    showScrollButton.value = scrollHeight - scrollTop - clientHeight > SCROLL_BUTTON_THRESHOLD;
  };

  const onScroll = () => {
    const { scrollTop, scrollHeight } = content.value;
    // moving up while the content didn't get shorter means the user scrolled, not the browser clamping
    if (scrollTop < lastScrollTop && scrollHeight >= lastScrollHeight) {
      followToBottom = false;
    }
    lastScrollTop = scrollTop;
    lastScrollHeight = scrollHeight;
    checkScrollPosition();
  };

  const getAnchorEl = () => (anchorIndex >= 0 ? list.value?.children[anchorIndex] : null);

  const updateSpacer = () => {
    const anchorEl = getAnchorEl();
    if (!content.value || !anchorEl) {
      spacerHeight.value = 0;
      return;
    }

    const heightFromAnchor = list.value.getBoundingClientRect().bottom - anchorEl.getBoundingClientRect().top;
    spacerHeight.value = Math.max(0, Math.ceil(content.value.clientHeight - heightFromAnchor));
  };

  const scrollAnchorToTop = async () => {
    pendingAnchor = false;
    updateSpacer();
    await nextTick();

    const anchorEl = getAnchorEl();
    if (!content.value || !anchorEl) {
      return;
    }

    const offset = anchorEl.getBoundingClientRect().top - content.value.getBoundingClientRect().top;
    content.value.scrollTo({ top: content.value.scrollTop + offset, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    followToBottom = true;
    content.value?.scrollTo({ top: content.value.scrollHeight, behavior: 'smooth' });
  };

  const prepareForNewMessage = () => {
    anchorIndex = messages.value.length;
    pendingAnchor = true;
    pendingScrollToBottom = false;
    followToBottom = false;
    showScrollButton.value = false;
  };

  const resetScrollState = () => {
    showScrollButton.value = false;
    followToBottom = false;
    if (pendingAnchor) {
      return;
    }

    anchorIndex = -1;
    spacerHeight.value = 0;
    pendingScrollToBottom = true;
  };

  const onMessagesRendered = () => {
    if (!content.value) {
      return;
    }

    if (pendingAnchor && messages.value.length > anchorIndex) {
      scrollAnchorToTop();
    } else if (pendingScrollToBottom) {
      pendingScrollToBottom = false;
      content.value.scrollTop = content.value.scrollHeight;
    }
    checkScrollPosition();
  };

  const resizeObserver = new ResizeObserver(async () => {
    updateSpacer();
    await nextTick();
    if (followToBottom && content.value) {
      content.value.scrollTop = content.value.scrollHeight;
    }
    checkScrollPosition();
  });

  watch(
    content,
    (el, prevEl) => {
      prevEl?.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      if (!el) {
        return;
      }

      lastScrollTop = el.scrollTop;
      lastScrollHeight = el.scrollHeight;
      el.addEventListener('scroll', onScroll, { passive: true });
      resizeObserver.observe(el);
      resizeObserver.observe(list.value);
      onMessagesRendered();
    },
    { flush: 'post' },
  );

  watch(() => messages.value.length, onMessagesRendered, { flush: 'post' });

  onBeforeUnmount(() => {
    resizeObserver.disconnect();
    content.value?.removeEventListener('scroll', onScroll);
  });

  return {
    content,
    list,
    spacerHeight,
    showScrollButton,
    scrollToBottom,
    prepareForNewMessage,
    resetScrollState,
  };
}
